import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { askConfirm, askDiscardChanges } from '../../../../shared/confirmDialog'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'
import TabBar from '../../../../UI/TabBar'

import BasicsTab from './BasicsTab'
import ContentTab from './ContentTab'
import QuestionsTab from './QuestionsTab'

import styles from './style.module.scss'

const TABS = [
	{ key: 'basics', label: 'Basic data' },
	{ key: 'content', label: 'Modules & lessons' },
	{ key: 'questions', label: 'Questions' }
]

/**
 * Einen Kurs bearbeiten: Grunddaten, Module mit Lektionen und Videos, Fragen.
 *
 * Jeder Teil speichert für sich — ein Modul, eine Lektion, eine Frage. Ein
 * einziger Speichern-Knopf über einem Kurs mit dreißig Videos und fünfzehn
 * Fragen hieße: Wer an Frage zwölf scheitert, verliert alles davor.
 *
 * Was ungespeichert ist, meldet jeder Teil über `markDirty`. Vor dem
 * Reiterwechsel und vor dem Verlassen fragt die Seite dann nach.
 */
export default function CourseEditor() {
	const { t } = useTranslation()
	const history = useHistory()
	const { id } = useParams()

	const [course, setCourse] = useState(null)
	const [fehler, setFehler] = useState(false)
	const [tab, setTab] = useState('basics')
	const scopeRef = useRef(null)
	useScrollTopOnTabChange(tab, scopeRef)

	const offen = useRef(new Set())
	const markDirty = useCallback((key, dirty) => {
		if (dirty) offen.current.add(key)
		else offen.current.delete(key)
	}, [])

	const reload = useCallback(
		() =>
			http
				.get(TApi.ADMIN_COURSE(id))
				.then(({ data }) => {
					setCourse(data)
					return data
				})
				.catch(() => setFehler(true)),
		[id]
	)

	useEffect(() => {
		reload()
	}, [reload])

	const erstFragen = async () => {
		if (offen.current.size === 0) return true
		const ok = await askDiscardChanges()
		if (ok) offen.current.clear()
		return ok
	}

	const wechseln = async key => {
		if (key === tab) return
		if (await erstFragen()) setTab(key)
	}

	const zurueck = async () => {
		if (await erstFragen()) history.push(TRoutes.ADMIN_COURSES)
	}

	const loeschen = async () => {
		const ok = await askConfirm({
			title: t('Delete course?'),
			text: t(
				'The course with all modules, lessons, videos and questions is deleted. A course that has already been completed cannot be deleted — archive it instead.'
			),
			confirmLabel: t('Delete'),
			tone: 'danger'
		})
		if (!ok) return
		http.delete(TApi.ADMIN_COURSE(id)).then(() => history.push(TRoutes.ADMIN_COURSES))
	}

	if (fehler) return <p className={styles.intro}>{t('The course could not be loaded.')}</p>
	if (!course) return <Loading />

	const fragen = (course.modules || []).reduce((summe, modul) => summe + (modul.questions || []).length, 0)

	return (
		<div className={styles.root} ref={scopeRef}>
			<MiniHeader className={styles.miniHeader} title={course.title} bg="#f8faff" goBack={TRoutes.ADMIN_COURSES}>
				<div className={styles.headActions}>
					<Btn theme="secondary" onClick={() => history.push(TRoutes.COURSE(course.id))}>
						{t('Preview')}
					</Btn>
					<Btn theme="secondary" onClick={zurueck}>
						{t('To the course list')}
					</Btn>
					<Btn theme="danger" onClick={loeschen}>
						{t('Delete')}
					</Btn>
				</div>
			</MiniHeader>

			<TabBar
				className={styles.tabs}
				tabs={TABS.map(eintrag =>
					eintrag.key === 'questions'
						? { ...eintrag, count: fragen }
						: eintrag.key === 'content'
						? { ...eintrag, count: (course.modules || []).length }
						: eintrag
				)}
				active={tab}
				onChange={wechseln}
			/>

			<div className={styles.block}>
				{tab === 'basics' && <BasicsTab course={course} onSaved={reload} markDirty={markDirty} />}
				{tab === 'content' && <ContentTab course={course} onChanged={reload} markDirty={markDirty} />}
				{tab === 'questions' && <QuestionsTab course={course} onChanged={reload} markDirty={markDirty} />}
			</div>
		</div>
	)
}
